import { type MouseEvent } from "react"
import { motion } from "framer-motion"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { EventoDataType } from "@/types/events"
import { Button } from "../../../components/ui/button"
import { getTime } from "@/utils/formatDate"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { Badge } from "@/components/ui/badge"
import { endOfDay, isAfter } from "date-fns"
import { isInscricaoEncerrada } from "@/utils/date"

interface BackofficeEventCardProps {
  evento: EventoDataType
  onCardClick?: (event: EventoDataType) => void
  onSubscribeEvent: (e: MouseEvent) => void
  onBatchSubscribe: (e: MouseEvent) => void
}

export function BackofficeEventCard({
  evento,
  onCardClick,
  onBatchSubscribe,
  onSubscribeEvent,
}: BackofficeEventCardProps) {
  const { user } = useAuth()
  const hasZoom = !!evento.even_link_zoom

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="h-full rounded-md"
    >
      <Card
        className="group relative flex h-full min-w-[300px] max-w-[340px] cursor-pointer flex-col overflow-hidden rounded-md border-white/20 bg-zinc-950/5 backdrop-blur-[10px] transition-all hover:border-primary/50"
        onClick={() => onCardClick?.(evento)}
      >
        <div className="absolute inset-0 -z-10 size-full bg-zinc-900/20 backdrop-blur-md" />

        <CardHeader className="space-y-2 pt-6">
          <div className="flex items-center justify-between">
            <Badge
              variant="outline"
              className="border-primary/50 text-[10px] font-bold uppercase leading-3 tracking-widest text-primary"
            >
              {evento.formato}
            </Badge>
            {evento.inscrito && (
              <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-500">
                <i className="fi fi-ts-check-circle" />
                Inscrito
              </span>
            )}
          </div>
          <CardTitle className="line-clamp-2 text-xl font-bold leading-tight text-white transition-colors group-hover:text-orange-500">
            {evento.descricao}
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <i className="fi fi-ts-marker text-primary/70" />
            <span className="truncate">{evento.local || "Local a definir"}</span>
          </div>
        </CardHeader>

        <CardContent className="grid grid-cols-2 gap-4 pb-0">
          <div className="flex items-center gap-2 text-sm text-zinc-200">
            <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-white/5">
              <i className="fi fi-ts-calendar text-primary" />
            </div>
            {new Date(evento.data_inicio).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-200">
            <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-white/5">
              <i className="fi fi-ts-clock text-primary" />
            </div>
            {getTime(evento.data_inicio)}h
          </div>
        </CardContent>

        <CardFooter className="mt-2 flex flex-col gap-2 border-t border-white/5 bg-black/20 p-4">
          {!evento.inscrito && (
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onSubscribeEvent(e)
              }}
              variant="default"
              className="w-full bg-gradient-to-tr from-orange-950 to-orange-500 font-bold uppercase tracking-tight text-orange-200 shadow-lg transition-all hover:scale-[1.02] hover:bg-orange-600 hover:shadow-orange-600/40"
            >
              Garantir minha vaga!
            </Button>
          )}
          {evento.inscrito && !hasZoom && (
            <Button
              disabled
              variant="secondary"
              className="w-full cursor-not-allowed font-bold uppercase tracking-tight"
            >
              Inscrito
            </Button>
          )}
          {evento.inscrito && hasZoom && (
            <Button
              onClick={(e) => {
                e.stopPropagation()
                window.open(evento.even_link_zoom!, "_blank", "noopener,noreferrer")
              }}
              variant="default"
              className="w-full bg-gradient-to-tr from-blue-900 to-blue-500 font-bold text-blue-100 shadow-lg transition-all hover:scale-[1.02] hover:shadow-blue-600/40"
            >
              <i className="fi fi-ts-video-camera mr-1 flex items-center justify-center" />
              Entrar no Zoom
            </Button>
          )}
          {user.pesAdmin &&
            !isInscricaoEncerrada(evento.data_limite) &&
            !isAfter(new Date(), endOfDay(new Date(evento.data_inicio))) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onBatchSubscribe(e)
                }}
                variant="ghost"
                size="sm"
                className="w-full text-xs font-semibold uppercase text-orange-500 hover:bg-white/5 hover:text-white"
              >
                <i className="fi fi-ts-login mr-2 flex items-center justify-center text-lg" />
                Inscrever participantes
              </Button>
            )}
        </CardFooter>
      </Card>
    </motion.div>
  )
}
